import React, { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { AsYouType } from 'libphonenumber-js'
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form'
import { usStatesSelector, caProvincesSelector } from '@src/selectors/app'
import { Validators } from '@src/utils'
import { AddressCountries } from '@src/types'
import { getCountryCodeByCountry } from '@src/utils/phoneInput'
import { useAddress } from '@src/hooks/useAddress'
import { isValidField } from '@src/utils/form'
import { Place } from '@src/services/GooglePlaces/GooglePlaces'
import { ControllerComponent } from '../Form'
import { Feature, useFeatureFlag } from '../FeatureFlag'
import { AddressSelect } from './AddressSelect'
import { AddressTextInput } from './AddressTextInput'
import { AddressAutoComplete } from './AddressAutoComplete'

const RESET_FIELDS_ON_COUNTRY_CHANGE = ['state', 'zipCode', 'street', 'street2'] as const

const COUNTRY_DEPENDENT_LABELS = {
  [AddressCountries.Us]: {
    state: 'State',
    zipCode: 'Zip Code',
  },
  [AddressCountries.Ca]: {
    state: 'Province',
    zipCode: 'Postal Code',
  },
  [AddressCountries.Gb]: {
    state: undefined,
    zipCode: 'Postal Code',
  },
}

export interface AddressFormData extends FieldValues {
  country: AddressCountries
  name: string
  street: string
  street2: string
  city: string
  state: string
  zipCode: string
  phoneNumber: string
}

interface AddressFormProps {
  allowCountryChange?: boolean
  showCountry?: boolean
  form: UseFormReturn<AddressFormData, any, AddressFormData>
}

export const AddressForm = (props: AddressFormProps) => {
  const { showCountry, allowCountryChange, form } = props
  const usStates = useSelector(usStatesSelector)
  const caProvinces = useSelector(caProvincesSelector)

  const didMount = useRef(false)

  const { control, setValue, clearErrors } = form

  const street = useWatch({ control, name: 'street' })
  const country = useWatch({ control, name: 'country' })
  const state = useWatch({ control, name: 'state' })
  const phoneNumber = useWatch({ control, name: 'phoneNumber' })

  const states = (country === AddressCountries.Us ? usStates : caProvinces).map((item) => ({
    text: item.abbreviation,
    value: item.abbreviation,
  }))

  const { placesMatchingSearch, debounceFetchPlaces, fetchAddressForPlace } = useAddress()

  useEffect(() => {
    debounceFetchPlaces.current(street, country)
  }, [street, country, debounceFetchPlaces])

  useEffect(() => {
    if (didMount.current) {
      RESET_FIELDS_ON_COUNTRY_CHANGE.forEach((field) => {
        setValue(field, '')
      })
    }
    didMount.current = true
  }, [country, setValue])

  const loadAddressData = async (place: Place) => {
    const address = await fetchAddressForPlace(place)

    clearErrors(['street', 'city', 'state', 'zipCode'])

    setValue('street', address.street, { shouldValidate: true, shouldTouch: true })
    setValue('street2', '')
    setValue('city', address.city, { shouldValidate: true, shouldTouch: true })
    setValue('state', address.state, { shouldValidate: true, shouldTouch: true })
    setValue('zipCode', address.zipCode, {
      shouldValidate: true,
      shouldTouch: true,
    })
  }

  const statesMatchingSearch = states.filter((item) =>
    item.text.toLowerCase().includes(state.toLowerCase()),
  )
  const isStateValid = (text: string) => {
    return states.some((item) => item.text.toLowerCase() === text.toLowerCase())
  }

  const usOption = { value: AddressCountries.Us, text: 'United States' }
  const caOption = { value: AddressCountries.Ca, text: 'Canada' }
  const ukOption = { value: AddressCountries.Gb, text: 'United Kingdom' }

  const countryOptions = [usOption, caOption]

  const enableUkSupport = useFeatureFlag(Feature.UkSupport)

  if (enableUkSupport) {
    countryOptions.push(ukOption)
  }

  const selectedCountryOption = countryOptions.find(({ value }) => value === country)

  const countryCode = getCountryCodeByCountry(country)

  return (
    <>
      {showCountry && (
        <ControllerComponent
          Component={allowCountryChange ? AddressSelect : AddressTextInput}
          useFieldRef={false}
          control={control}
          required
          label="Country"
          name="country"
          selectedOption={selectedCountryOption}
          value={selectedCountryOption?.text}
          options={countryOptions}
          disabled={!allowCountryChange}
        />
      )}
      <ControllerComponent
        control={control}
        Component={AddressTextInput}
        name="name"
        label="Full Name"
        placeholder="Jeremy Clarks"
        required
        validate={(value) => isValidField(country)(value, 'name')}
      />
      <ControllerComponent
        control={control}
        Component={AddressAutoComplete}
        name="street"
        label="Street"
        placeholder="53 Fox Hill Lane"
        required
        validate={(value) => isValidField(country)(value, 'street')}
        onSelect={(idx: number) => {
          loadAddressData(placesMatchingSearch[idx])
        }}
        options={placesMatchingSearch.map((place) => ({
          value: place.place_id,
          text: place.description,
        }))}
      />
      <ControllerComponent
        control={control}
        Component={AddressTextInput}
        label="Street 2"
        name="street2"
        placeholder="apt. 52"
      />
      <ControllerComponent
        control={control}
        Component={AddressTextInput}
        label="City"
        name="city"
        placeholder="Center Conway"
        required
        validate={(value) => isValidField(country)(value, 'city')}
      />
      {COUNTRY_DEPENDENT_LABELS[country].state && (
        <ControllerComponent
          control={control}
          Component={AddressAutoComplete}
          label={COUNTRY_DEPENDENT_LABELS[country].state}
          placeholder="NH"
          name="state"
          required
          options={statesMatchingSearch}
          validate={(value) => isStateValid(value)}
        />
      )}
      <ControllerComponent
        control={control}
        Component={AddressTextInput}
        label={COUNTRY_DEPENDENT_LABELS[country].zipCode}
        name="zipCode"
        required
        placeholder="00001"
        validate={(value) => isValidField(country)(value, 'zipCode')}
      />
      <ControllerComponent
        control={control}
        Component={AddressTextInput}
        label="Phone Number"
        name="phoneNumber"
        required
        placeholder="Phone Number"
        keyboardType="phone-pad"
        validate={(value) => Validators.PhoneNumberValidator(value, countryCode)}
        onChangeText={(text: string) => {
          const newText = text.trim()
          const formattedNewText = new AsYouType(countryCode).input(newText)

          if (newText.length < (phoneNumber || '').length && formattedNewText === phoneNumber) {
            // do not re-format on character removal
            return newText
          }

          return formattedNewText
        }}
      />
    </>
  )
}
